/**
 * Production Order List Page
 * Manufacturing Module — card-based list with status tabs, search, progress bars
 *
 * Features:
 *   - Status tabs: ALL, DRAFT, PLANNED, IN_PROGRESS, COMPLETED, CANCELLED (with counts)
 *   - Card-based layout with progress bar, phase info, date range
 *   - Search by order number
 *   - Actions: View details, Start, Complete
 */

import type { ProductionOrder, ProductionOrderStatus } from '@/api/manufacturing';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useProductionOrders } from '@/hooks/use-manufacturing';
import { useTranslation } from '@kgc/i18n';
import { Badge } from '@kgc/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Status Config ──────────────────────────────────────────────────────────

type TabKey = 'ALL' | ProductionOrderStatus;

const ORDER_STATUSES: ProductionOrderStatus[] = [
  'DRAFT',
  'PLANNED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
];

const STATUS_COLORS: Record<ProductionOrderStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  PLANNED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  IN_PROGRESS: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('hu-HU');
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ProductionOrderListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  // Build API filters
  const apiFilters = useMemo(() => {
    const filters: { status?: ProductionOrderStatus; search?: string } = {};
    if (activeTab !== 'ALL') {
      filters.status = activeTab;
    }
    if (searchTerm) {
      filters.search = searchTerm;
    }
    return filters;
  }, [activeTab, searchTerm]);

  const { orders, isLoading, error, refetch } = useProductionOrders(apiFilters);

  // Count per status
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: orders.length };
    for (const status of ORDER_STATUSES) {
      counts[status] = orders.filter(o => o.status === status).length;
    }
    return counts;
  }, [orders]);

  const tabs: { key: TabKey; labelKey: string }[] = [
    { key: 'ALL', labelKey: 'common.all' },
    ...ORDER_STATUSES.map(status => ({
      key: status as TabKey,
      labelKey: `manufacturing.status.${status.toLowerCase()}`,
    })),
  ];

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                {t('manufacturing.productionOrder.title')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('manufacturing.productionOrder.subtitle')}
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/manufacturing/orders/new')}
            className="bg-primary hover:bg-primary/90"
          >
            + {t('manufacturing.productionOrder.createNew')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Status Tabs */}
        <div className="mb-6 flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-foreground shadow dark:bg-[#1a4f5c]'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(tab.labelKey)}
              <span
                className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-xs font-medium ${
                  activeTab === tab.key
                    ? 'bg-primary/10 text-primary'
                    : 'bg-gray-200 text-muted-foreground dark:bg-gray-700'
                }`}
              >
                {statusCounts[tab.key] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <Input
            placeholder={t('manufacturing.productionOrder.searchPlaceholder')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="py-12 text-center text-muted-foreground">{t('common.loading')}</div>
        )}

        {/* Error */}
        {error && (
          <div className="py-12 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Button variant="ghost" onClick={() => void refetch()} className="mt-2">
              {t('common.retry')}
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && orders.length === 0 && (
          <div className="py-12 text-center text-muted-foreground">
            <p>{t('manufacturing.productionOrder.noOrders')}</p>
          </div>
        )}

        {/* Order Cards Grid */}
        {!isLoading && !error && orders.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {orders.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                onDetails={() => navigate(`/manufacturing/orders/${order.id}`)}
                t={t}
              />
            ))}
          </div>
        )}

        {/* Count */}
        {!isLoading && orders.length > 0 && (
          <p className="mt-4 text-sm text-muted-foreground">
            {t('manufacturing.productionOrder.displayedCount', {
              count: String(orders.length),
            })}
          </p>
        )}
      </main>
    </div>
  );
}

// ─── Order Card ─────────────────────────────────────────────────────────────

interface OrderCardProps {
  order: ProductionOrder;
  onDetails: () => void;
  t: (key: string, params?: Record<string, string>) => string;
}

function OrderCard({ order, onDetails, t }: OrderCardProps) {
  const progressPercent =
    order.totalPhases > 0 ? Math.round((order.completedPhases / order.totalPhases) * 100) : 0;

  return (
    <Card className="group cursor-pointer transition-shadow hover:shadow-md" onClick={onDetails}>
      <CardContent className="pt-6">
        {/* Header: Order number + Status */}
        <div className="mb-3 flex items-start justify-between">
          <h3 className="text-lg font-semibold text-foreground">{order.orderNumber}</h3>
          <Badge className={STATUS_COLORS[order.status]}>
            {t(`manufacturing.status.${order.status.toLowerCase()}`)}
          </Badge>
        </div>

        {/* BOM + Product */}
        <div className="mb-3 space-y-1 text-sm text-muted-foreground">
          <div>
            <span className="font-medium text-foreground">BOM:</span> {order.bomCode}
          </div>
          <div>
            <span className="font-medium text-foreground">
              {t('manufacturing.productionOrder.product')}:
            </span>{' '}
            {order.outputProductName}
          </div>
        </div>

        {/* Quantity */}
        <div className="mb-3 flex items-baseline gap-2 text-sm">
          <span className="text-muted-foreground">
            {t('manufacturing.productionOrder.planned')}:{' '}
            <span className="font-medium text-foreground">{order.plannedQuantity}</span>
          </span>
          {order.actualQuantity > 0 && (
            <span className="text-muted-foreground">
              {t('manufacturing.productionOrder.actual')}:{' '}
              <span className="font-medium text-foreground">{order.actualQuantity}</span>
            </span>
          )}
        </div>

        {/* Date range */}
        <div className="mb-3 text-xs text-muted-foreground">
          {formatDate(order.plannedStart)} — {formatDate(order.plannedEnd)}
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {t('manufacturing.productionOrder.phases')}: {order.completedPhases}/
              {order.totalPhases}
            </span>
            <span>{progressPercent}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className={`h-full rounded-full transition-all ${
                progressPercent === 100 ? 'bg-green-500 dark:bg-green-400' : 'bg-primary'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 border-t pt-3 dark:border-slate-700">
          <Button
            variant="ghost"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              onDetails();
            }}
          >
            {t('common.details')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
